import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { RevealLine, FadeUp } from "../components/Reveal";
import ArticleArt from "../components/ArticleArt";
import { ARTICLES, CALENDLY_URL } from "../data/content";
import { ARTICLE_BODIES } from "../data/articles";

export default function BlogPage() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  const body = ARTICLE_BODIES[slug];
  if (!article || !body) return <Navigate to="/#insights" replace />;
  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  const next = ARTICLES[(idx + 1) % ARTICLES.length];

  return (
    <main className="pt-32 pb-16" data-testid={`blog-page-${slug}`}>
      <article className="max-w-3xl mx-auto px-6">
        <FadeUp y={12}>
          <Link to="/#insights" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-green transition-colors duration-200 mb-10" data-testid="blog-back-link">
            <ArrowLeft size={13} /> All insights
          </Link>
        </FadeUp>
        <div className="flex items-center gap-4 mb-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-green bg-green-lt px-3 py-1 rounded-full">{article.tag}</span>
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-soft"><Clock size={11} /> {article.readTime} read</span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-soft" data-testid="blog-author">By {article.author}</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-ink mb-8" data-testid="blog-title">
          <RevealLine animateOnLoad>{article.title}</RevealLine>
        </h1>
        <FadeUp className="font-serif text-xl italic text-ink-mid leading-relaxed mb-10 border-l-2 border-green pl-6">
          {article.excerpt}
        </FadeUp>
        <FadeUp className="mb-12">
          <ArticleArt slug={slug} showTitle={false} className="h-40 sm:h-48 rounded-2xl" />
        </FadeUp>
        <div className="space-y-12">
          {body.map((section, i) => (
            <FadeUp key={i} delay={0.05}>
              <h2 className="font-serif text-2xl text-ink mb-4">{section.h}</h2>
              <p className="text-ink-mid leading-[1.85] text-[15px]">{section.p}</p>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-16 bg-forest grain relative rounded-2xl p-10 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mint mb-4">CFOSutra</p>
          <p className="font-serif text-2xl text-paper mb-8">Want numbers like these working for <span className="italic">your</span> business?</p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="blog-cta-btn" className="inline-flex items-center gap-2 bg-mint text-forest text-sm font-medium px-7 py-3.5 rounded-sm hover:bg-paper transition-colors duration-200">
            Book a free 30-minute call <ArrowUpRight size={14} />
          </a>
        </FadeUp>
        <FadeUp className="mt-16 pt-10 border-t border-rule">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-soft mb-4">Read next</p>
          <Link to={`/insights/${next.slug}`} className="group block" data-testid="blog-next-link">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-green">{next.tag}</span>
            <h3 className="font-serif text-2xl text-ink mt-2 group-hover:text-green transition-colors duration-300">{next.title}</h3>
          </Link>
        </FadeUp>
      </article>
    </main>
  );
}
